import { ShowChart } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CustomButton from "../components/Button";
import CustomCardList from "../components/Cards";
import PageLoader from "../components/Loader";
import BarGraphDialog from "../components/MonthWiseAnalytics";
import Title from "../components/PageTitle";
import CustomTable from "../components/Table";
import { useToast } from "../components/Toast";
import { getUserInfo } from "../store/rootReducer";
import { useSelector } from "react-redux";
const AnalyticsTable = () => {
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("limit")) || 10,
  );
  const [openTopDoctors, setOpenTopDoctors] = useState(false);
  const [topDoctorMessage, setTopDoctorMessage] = useState("");
  const [totalCount, setTotalCount] = useState(0); // Total number of doctors (for pagination)
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [kiosk, setKiosk] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("token");
  const [showMonthwise, setMonthwise] = useState(false);
  const handleCloseMonthWise = () => setMonthwise(false);
  const [selectedKioskId, setSelectedKioskId] = useState("");
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [duration, setDuration] = useState("activeInLast7Days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limit, setLimit] = useState(10);
  const userInfo = useSelector(getUserInfo);

  const updateURLParams = (newFilters) => {
    setSearchParams({
      q: newFilters.searchQuery || "",
      page: newFilters.page || 1,
      limit: newFilters.rowsPerPage || 10,
    });
  };
  useEffect(() => {
    updateURLParams({
      searchQuery,
      page,
      rowsPerPage,
    });
  }, [searchQuery, page, rowsPerPage]);
  useEffect(() => {
    getKiosk(page, rowsPerPage);
  }, [page, rowsPerPage]);
  const requestTopDoc = async () => {
    try {
      const lastRequest = localStorage.getItem("topDoctorsRequestTime");

      const FIFTEEN_MINUTES = 15 * 60 * 1000;

      if (lastRequest) {
        const difference = Date.now() - Number(lastRequest);

        if (difference < FIFTEEN_MINUTES) {
          setTopDoctorMessage(
            "Your request is already being processed. Please check your email for the report.",
          );

          Toast.error(
            "Your request is already being processed. Please check your email for the report.",
          );

          return;
        }
      }

      // SET EVERYTHING BEFORE API CALL
      localStorage.setItem("topDoctorsRequestTime", Date.now().toString());

      setTopDoctorMessage(
        "Your request has been submitted successfully. The report will be shared with you via email shortly.",
      );
      Toast.success(
        "Your request has been submitted successfully. The report will be shared with you via email shortly.",
      );

      setOpenTopDoctors(false);

      // Background API call
      apiService
        .requestTopDoctors({
          startDate,
          endDate,
          limit,
          email: userInfo.email,
          token,
        })
        .then((response) => {
          console.log(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      Toast.error("Something went wrong ");
    }
  };
  const getKiosk = async (page, limit) => {
    setIsLoading(true);
    try {
      const { mappedDoctors, count } = await apiService.fetchKiosk(
        auth,
        page,
        limit,
      );
      setKiosk(mappedDoctors);

      setFilteredDoctors(mappedDoctors);
      setTotalCount(count); // Set the total count
      setIsLoading(false);
    } catch (error) {
      Toast.error("Error while Fetching");
    } finally {
      setIsLoading(false);
    }
  };
  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1); // Set the new page
  };
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Update rows per page when changed
    setPage(1); // Reset to page 1 when rows per page change
  };

  useEffect(() => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(async () => {
      try {
        const { mappedDoctors, count } = await apiService.fetchKiosk(
          token,
          page,
          10,
          searchQuery,
        );
        setTotalCount(count);
        setFilteredDoctors(mappedDoctors);
      } catch (error) {
        console.error("Error fetching Doctors:", error);
        Toast.error("Error while Fetching");
      }
    }, 500); // 500ms debounce

    setTypingTimeout(timeout);

    // Cleanup
    return () => clearTimeout(timeout);
  }, [searchQuery, page]);

  const handleSearch = (event) => {
    const keyword = event.target.value.toLowerCase();
    setSearchQuery(keyword); // Only set the query
  };

  const fields = [
    { label: "Clinic Name", key: "clinicName" },
    { label: "Location", key: "location" },
  ];

  const columns = [
    {
      field: "clinicName",
      headerName: "Clinic Name",
      align: "center",
    },
    {
      field: "doctor",
      headerName: "Doctor Name",
      align: "center",
    },
    {
      field: "activityStatus",
      headerName: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 120,
          }}
        >
          <Typography variant="caption" fontWeight="bold">
            Inactive Status
          </Typography>
          <FormControl size="small" sx={{ mt: 1, minWidth: 100 }}>
            <Select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              displayEmpty
              variant="outlined"
              sx={{
                fontSize: "12px",
                backgroundColor: "#fff",
                height: "30px",
                ".MuiSelect-select": {
                  padding: "4px 8px",
                },
              }}
            >
              <MenuItem value="activeInLast7Days">7 Days</MenuItem>
              <MenuItem value="activeInLast30Days">30 Days</MenuItem>
              <MenuItem value="activeInLast60Days">60 Days</MenuItem>
              <MenuItem value="activeInLast90Days">90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      ),
      align: "center",
      renderCell: (value, row) => {
        const isActive = row?.[duration] === true;

        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor: isActive ? "#d4edda" : "#f8d7da",
              color: isActive ? "#155724" : "#721c24",
              fontWeight: "bold",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
  ];
  return (
    <div>
      <Paper sx={{ padding: 2 }}>
        <Title
          text="Analytics"
          showActions={true}
          actions={[
            {
              label: "Get Top Doctors ",
              onClick: () => setOpenTopDoctors(true),
            },
          ]}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginBottom: 2,
            width: "100%",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            fullWidth
            sx={{ marginBottom: 2, marginTop: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isMobile ? (
          // Card layout for mobile
          <CustomCardList
            page={page - 1} // Adjust for zero-based indexing for `CustomTable`
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            fields={fields}
            data={filteredDoctors}
            onCardClick={(doctor) => {
              setMonthwise(true);
              setSelectedKioskId(doctor.id); // Assuming `doctor` is the correct object
            }}
          />
        ) : (
          <CustomTable
            columns={columns}
            data={filteredDoctors}
            onRowClick={(row) => {
              setMonthwise(true);
              setSelectedKioskId(row.id);
            }}
            actions={[
              {
                Component: CustomButton,
                props: { text: <ShowChart />, width: "90px", height: "40px" },
                onClick: (row) => {
                  setMonthwise(true);
                  setSelectedKioskId(row.id);
                },
              },
            ]}
            page={page - 1} // Adjust for zero-based indexing for `CustomTable`
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
        {showMonthwise && (
          <BarGraphDialog
            open={showMonthwise}
            onClose={handleCloseMonthWise}
            kioskId={selectedKioskId}
          />
        )}
      </Paper>
      <Dialog
        open={openTopDoctors}
        onClose={() => setOpenTopDoctors(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Get Top Doctors</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            <TextField
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            <TextField
              label="Limit"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              fullWidth
            />

            {topDoctorMessage && (
              <Typography
                sx={{
                  color: "primary.main",
                  fontSize: "14px",
                }}
              >
                {topDoctorMessage}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenTopDoctors(false)}>Cancel</Button>

          <Button variant="contained" onClick={requestTopDoc}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {isLoading && <PageLoader />}
    </div>
  );
};
export default AnalyticsTable;
